import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";
import { analyticsService, notificationManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { ToastContext } from "@ui/components/common/toast-provider";
import { ConfirmActionDialog } from "@ui/pages/program-builder/dialog/confirm-dialog";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useContext } from "react";

export interface MarkAllNotificationsAsReadDialogProps extends DialogProps {
	unreadNotificationCount: number;
}

export const MarkAllNotificationsAsReadDialog = ({
	unreadNotificationCount,
	...otherProps
}: MarkAllNotificationsAsReadDialogProps): JSX.Element => {
	const { format } = useI18n();
	const { showToast } = useContext(ToastContext);

	const [markAllAsRead, isLoading] = useAsyncOperation(
		useCallback(async () => {
			await notificationManager.markAllAsRead();
			analyticsService.trackEvent({ category: AnalyticsCategory.NOTIFICATIONS, action: "notificationAllRead" });
			showToast(
				"",
				format("notificationCenter.markAllAsRead.toastSuccess.detail", { count: unreadNotificationCount }),
				"success",
			);
			otherProps.onHide();
		}, [format, otherProps, showToast, unreadNotificationCount]),
		{},
	);

	return (
		<ConfirmActionDialog
			{...otherProps}
			onCancel={otherProps.onHide}
			onConfirm={markAllAsRead}
			confirmLoading={isLoading}
			confirmButtonStyle="primary"
			message={format("notificationCenter.markAllAsRead.dialog.description")}
			title={format("notificationCenter.markAllAsRead.dialog.header", { count: unreadNotificationCount })}
		/>
	);
};
